import type { ButtonHTMLAttributes, ReactNode } from "react";
import { default as cn } from "@/lib/clsx";

const variants = {
  primary: "bg-primary text-text-inverse shadow-button hover:opacity-90",

  outline:
    "border border-primary bg-transparent text-primary hover:bg-secondary",

  ghost: "text-text-main hover:bg-secondary",

  danger: "bg-status-error text-white shadow-button hover:opacity-90",

  accent: "bg-primary text-text-inverse shadow-button hover:opacity-90",

  neutral:
    "border border-border-main bg-bg-surface text-text-main hover:border-primary",

  empty: "",
} as const;

const sizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export default function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium" +
    "transition-all duration-200 " +
    "focus:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ring-main focus-visible:ring-offset-2 " +
    "focus-visible:ring-offset-bg " +
    "disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <button
      className={cn(
        base,
        variants[variant],
        sizes[size],
        fullWidth && "w-full",
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}

      {leftIcon && <span className="flex items-center">{leftIcon}</span>}

      {children}

      {rightIcon && <span className="flex items-center">{rightIcon}</span>}
    </button>
  );
}
